import type { ReactNode, HTMLAttributes } from 'react';
import { Icon } from '../../basic/Icon';
import styles from './MobileCard.module.css';

export interface MobileCardDetail {
  label: string;
  value: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
}

export interface MobileCardProps extends HTMLAttributes<HTMLDivElement> {
  header: {
    primary: string | ReactNode;
    secondary?: string | ReactNode;
  };
  details: MobileCardDetail[];
  isExpanded?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
}

export function MobileCard({
  header,
  details,
  isExpanded = false,
  onToggle,
  onClick,
  className,
  id,
  ...props
}: MobileCardProps) {
  const cardId = id || `mobile-card-${Math.random().toString(36).substr(2, 9)}`;
  const detailsId = `mobile-card-details-${cardId}`;

  const cardClasses = [
    styles.card,
    isExpanded && styles.expanded,
    onClick && styles.clickable,
    className,
  ].filter(Boolean).join(' ');

  const handleHeaderClick = () => {
    // Navegar cuando se hace click en el header
    if (onClick) {
      onClick();
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    // Prevenir que el click se propague al header
    e.stopPropagation();
    // Hacer toggle si está disponible
    if (onToggle) {
      onToggle();
    }
  };

  const chevronClasses = [
    styles.chevron,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClasses}
      id={cardId}
      {...props}
    >
      {/* Header - always visible */}
      <button
        type="button"
        className={styles.header}
        onClick={handleHeaderClick}
        aria-expanded={isExpanded}
        aria-controls={detailsId}
      >
        <div className={styles.headerContent}>
          <div className={styles.primary}>{header.primary}</div>
          <div className={styles.secondary}>{header.secondary}</div>
        </div>
        <Icon
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size="sm"
          className={chevronClasses}
          onClick={handleChevronClick}
        />
      </button>

      {/* Details - expandable */}
      {isExpanded && (
        <div
          id={detailsId}
          className={styles.details}
        >
          {details.map((detail, idx) => {
            const detailValueClasses = [
              styles.detailValue,
              detail.variant && detail.variant !== 'default' && styles[detail.variant],
            ].filter(Boolean).join(' ');

            return (
              <div key={idx} className={styles.detailRow}>
                <span className={styles.detailLabel}>{detail.label}</span>
                <span className={detailValueClasses}>{detail.value}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

MobileCard.displayName = 'MobileCard';
